/**
 * Chat — Phase 1 web chat (the P1 demo surface).
 *
 * Flow:
 *   load -> GET /api/chat/suggestions -> render starter prompts
 *   send -> POST /api/chat {message} -> append the assistant answer
 *
 * Phase 1 has no retrieval, so answers come straight from the configured LLM
 * provider (deterministic mock in the demo). Every turn is recorded server-side
 * to the hash-chained audit trail, which the Audit page renders.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { MARKDOWN_BLOCKED } from '../lib/markdownPolicy';
import { api, ApiError } from '../lib/api';

interface ChatResponse {
  answer: string;
  matched: boolean;
  retrieved: boolean;
  suggestions: string[];
}

interface SuggestionsResponse {
  suggestions: string[];
}

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export function Chat(): ReactNode {
  const [message, setMessage] = useState('');
  const [turns, setTurns] = useState<ChatTurn[]>([]);

  const suggestionsQuery = useQuery({
    queryKey: ['chat-suggestions'],
    queryFn: () => api.get<SuggestionsResponse>('/chat/suggestions'),
  });

  const mutation = useMutation<ChatResponse, ApiError, string>({
    // Replay the visible thread (recent turns, content-capped to match the
    // API contract) so the model sees what the user sees.
    mutationFn: (msg: string) =>
      api.post<ChatResponse>('/chat', {
        message: msg,
        history: turns
          .slice(-20)
          .map((t) => ({ role: t.role, content: t.content.slice(0, 8000) })),
      }),
    onSuccess: (data) => {
      setTurns((prev) => [...prev, { role: 'assistant', content: data.answer }]);
    },
  });

  function send(msg: string): void {
    const trimmed = msg.trim();
    if (!trimmed || mutation.isPending) return;
    setTurns((prev) => [...prev, { role: 'user', content: trimmed }]);
    setMessage('');
    mutation.mutate(trimmed);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    send(message);
  }

  const suggestions = suggestionsQuery.data?.suggestions ?? [];

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 animate-fade-in">
      <header>
        <h2 className="text-xl font-semibold text-navy-900">Chat</h2>
        <p className="mt-1 text-sm text-steel-600">
          Ask the Trident AI assistant. Phase 1 answers from general knowledge —
          permission-aware document retrieval lives on the Retrieval page. Every
          turn is recorded to the audit trail.
        </p>
      </header>

      {turns.length === 0 && suggestions.length > 0 && (
        <div className="card">
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-steel-500">
            Try one of these
          </h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                data-testid="chat-suggestion"
                onClick={() => send(s)}
                className="rounded-full border border-steel-200 bg-steel-50 px-3 py-1 text-xs text-steel-600 hover:bg-steel-100"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {turns.length > 0 && (
        <section className="flex flex-col gap-3" data-testid="chat-thread">
          {turns.map((turn, idx) => (
            <article
              key={idx}
              data-testid={`chat-turn-${turn.role}`}
              className={
                turn.role === 'user'
                  ? 'self-end max-w-[85%] rounded-lg bg-navy-700 px-4 py-2 text-sm text-white'
                  : 'self-start max-w-[85%] card'
              }
            >
              {turn.role === 'assistant' ? (
                <div className="prose prose-sm max-w-none text-navy-900">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    disallowedElements={MARKDOWN_BLOCKED}
                  >
                    {turn.content}
                  </ReactMarkdown>
                </div>
              ) : (
                turn.content
              )}
            </article>
          ))}
          {mutation.isPending && (
            <p className="self-start text-sm text-steel-500" data-testid="chat-pending">
              Assistant is typing…
            </p>
          )}
        </section>
      )}

      {mutation.isError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Something went wrong sending your message.'}
        </div>
      )}

      <form onSubmit={onSubmit} className="card flex flex-col gap-3">
        <label htmlFor="chat-message" className="sr-only">
          Message
        </label>
        <textarea
          id="chat-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          placeholder="Ask the assistant a question…"
          className="w-full resize-y rounded-lg border border-steel-300 px-3 py-2 text-sm focus:border-navy-500 focus:outline-none focus:ring-1 focus:ring-navy-500"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-primary disabled:opacity-50"
            disabled={mutation.isPending || message.trim().length === 0}
          >
            {mutation.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Chat;
